import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';

import Icon from '../../components/AppIcon';
import HeroSection from './components/HeroSection';
import FeatureSection from './components/FeatureSection';
import SocialProofSection from './components/SocialProofSection';
import PreviewSection from './components/PreviewSection';
import CTASection from './components/CTASection';

const LandingPage = () => {
  const navigate = useNavigate();

  // Platform statistics for hero section
  const platformStats = {
    totalDonated: 1250000,
    individualDonations: 850000,
    businessDonations: 400000,
    activeDonors: 15420,
    causesSupported: 340,
    nonprofitsPartnered: 180
  };

  // Featured causes preview
  const featuredCauses = [
    {
      id: 1,
      name: "Clean Water Initiative",
      category: "Water & Sanitation",
      image: "https://images.unsplash.com/photo-1594736797933-d0401ba2fe65?w=400&h=300&fit=crop",
      alt: "Hands cupping clean flowing water from a natural spring",
      description: "Providing clean water access to remote communities",
      raised: 45000,
      goal: 75000,
      donors: 450
    },
    {
      id: 2,
      name: "Education for All",
      category: "Education",
      image: "https://images.unsplash.com/photo-1497486751825-1233686d5d80?w=400&h=300&fit=crop",
      alt: "Students in a classroom engaged in learning activities",
      description: "Supporting schools in underserved areas",
      raised: 62000,
      goal: 100000,
      donors: 720
    },
    {
      id: 3,
      name: "Emergency Relief Fund",
      category: "Disaster Relief",
      image: "https://images.unsplash.com/photo-1584515933487-779824d29309?w=400&h=300&fit=crop",
      alt: "Emergency responders providing aid during disaster relief operations",
      description: "Rapid response to natural disasters worldwide",
      raised: 88000,
      goal: 150000,
      donors: 920
    }
  ];

  // Recent community activities
  const recentActivities = [
    {
      donor: "Sarah J.",
      action: "donated to",
      cause: "Water for Life Foundation",
      amount: 75,
      timeAgo: "30 minutes ago"
    },
    {
      donor: "Michael R.",
      action: "joined",
      cause: "Holiday Giving Challenge",
      timeAgo: "1 hour ago"
    },
    {
      donor: "Emily C.",
      action: "donated to",
      cause: "Global Emergency Response",
      amount: 200,
      timeAgo: "2 hours ago"
    }
  ];

  const handleSignUp = () => {
    navigate('/register');
  };

  const handleLogin = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Use consistent Header component */}
      <Header />

      {/* Hero Section */}
      <HeroSection 
        stats={platformStats}
        onSignUp={handleSignUp}
        onLogin={handleLogin}
      />

      {/* Features Section */}
      <FeatureSection />

      {/* Social Proof Section */}
      <SocialProofSection activities={recentActivities} />

      {/* Preview Section */}
      <PreviewSection causes={featuredCauses} />

      {/* Final CTA Section */}
      <CTASection onSignUp={handleSignUp} />

      {/* Footer */}
      <footer className="bg-card border-t border-border py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Brand */}
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                  <Icon name="Heart" size={24} color="white" />
                </div>
                <span className="text-xl font-bold text-black">Share Good</span>
              </div>
              <p className="text-black mb-4 max-w-md">
                Empowering individuals and companies to make a positive impact through strategic charitable giving and community engagement.
              </p>
              <div className="flex space-x-4">
                <button className="w-10 h-10 rounded-full bg-muted hover:bg-primary transition-colors flex items-center justify-center">
                  <Icon name="Twitter" size={18} />
                </button>
                <button className="w-10 h-10 rounded-full bg-muted hover:bg-primary transition-colors flex items-center justify-center">
                  <Icon name="Facebook" size={18} />
                </button>
                <button className="w-10 h-10 rounded-full bg-muted hover:bg-primary transition-colors flex items-center justify-center">
                  <Icon name="Instagram" size={18} />
                </button>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-black font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li>
                  <button onClick={handleSignUp} className="text-black hover:text-primary transition-colors">
                    Get Started
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/cause-discovery')} className="text-black hover:text-primary transition-colors">
                    Browse Causes
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/about')} className="text-black hover:text-primary transition-colors">
                    About Us
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/contact')} className="text-black hover:text-primary transition-colors">
                    Contact
                  </button>
                </li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h4 className="text-black font-semibold mb-4">Support</h4>
              <ul className="space-y-2">
                <li>
                  <button onClick={() => navigate('/contact')} className="text-black hover:text-primary transition-colors">
                    Help Center
                  </button>
                </li>
                <li>
                  <button className="text-black hover:text-primary transition-colors">
                    Privacy Policy
                  </button>
                </li>
                <li>
                  <button className="text-black hover:text-primary transition-colors">
                    Terms of Service
                  </button>
                </li>
                <li>
                  <button className="text-black hover:text-primary transition-colors">
                    For Nonprofits
                  </button>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-border mt-8 pt-8 text-center text-black">
            <p>&copy; 2025 Share Good. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;